/* Per-cell feedback. PalinDragon uses a simpler, more honest rule than
   Wordle: gray means the letter does not appear in the solution at all.
   Wordle's letter-counting wrinkle (where extra copies turn gray once the
   "in-solution" copies are exhausted) is wrong here — given the mirror
   constraint, players need to know unambiguously whether a letter is in
   play, and the symmetry already fixes the count question. */

/** "empty" is reserved for cells the player left blank at submit time —
   they render black so the player visibly registers the cost of skipping. */
export type Mark = "green" | "yellow" | "gray" | "empty";

export function scoreGuess(
  guess: readonly (string | null)[],
  solution: readonly string[],
): Mark[] {
  if (guess.length !== solution.length) {
    throw new Error(`length mismatch: guess=${guess.length}, solution=${solution.length}`);
  }
  const inSolution = new Set(solution);
  const marks: Mark[] = new Array(guess.length);
  for (let i = 0; i < guess.length; i++) {
    const ch = guess[i];
    if (ch == null) marks[i] = "empty";
    else if (ch === solution[i]) marks[i] = "green";
    else if (inSolution.has(ch)) marks[i] = "yellow";
    else marks[i] = "gray";
  }
  return marks;
}

const RANK: Record<Mark, number> = { empty: 0, gray: 1, yellow: 2, green: 3 };

/** Aggregate the best mark seen so far per letter across all submitted guesses.
   Returned map only contains entries for letters that have been guessed. */
export function aggregateKeyboard(
  guesses: readonly (readonly (string | null)[])[],
  marks: readonly (readonly Mark[])[],
): Map<string, Mark> {
  const out = new Map<string, Mark>();
  for (let g = 0; g < guesses.length; g++) {
    const guess = guesses[g]!;
    const guessMarks = marks[g]!;
    for (let i = 0; i < guess.length; i++) {
      const ch = guess[i];
      if (ch == null) continue;
      const m = guessMarks[i]!;
      const prev = out.get(ch);
      if (!prev || RANK[m] > RANK[prev]) out.set(ch, m);
    }
  }
  return out;
}
